import { CapabilityButton } from "../CapabilityButton";
import { permissionNames as P } from "../../auth/permissions.generated";
import React, { useEffect, useMemo, useState } from "react";
import {
  Check,
  Copy,
  Download,
  Edit2,
  Image as ImageIcon,
  Loader2,
  Palette,
  Save,
  Sparkles,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useCampaignApi } from "../../services/apiService";
import type { NavigationItem } from "../Navigation";
import type { StyleReference, StyleReferenceData, StyleForgeInlineImage } from "../../types/styleForge";

interface StyleForgeStudioProps {
  onNavigate: (view: NavigationItem) => void;
}

const aspectRatioGroups = [
  {
    label: "Common",
    options: [
      { value: "1:1", label: "Square (1:1)" },
      { value: "16:9", label: "Landscape (16:9)" },
      { value: "9:16", label: "Portrait (9:16)" },
      { value: "4:3", label: "Classic Landscape (4:3)" },
      { value: "3:4", label: "Classic Portrait (3:4)" },
      { value: "4:1", label: "Wide Banner (4:1)" },
      { value: "1:4", label: "Tall Banner (1:4)" },
    ],
  },
  {
    label: "LinkedIn",
    options: [
      { value: "1:1", label: "Post Square (1080x1080)" },
      { value: "1200:627", label: "Post Landscape (1200x627)" },
      { value: "1128:191", label: "Company Page Banner (1128x191)" },
      { value: "1200:628", label: "Single Image Ad (1200x628)" },
    ],
  },
  {
    label: "Instagram",
    options: [
      { value: "1:1", label: "Post Square (1080x1080)" },
      { value: "1080:1350", label: "Post Portrait (1080x1350)" },
      { value: "1080:566", label: "Post Landscape (1080x566)" },
      { value: "9:16", label: "Story / Reel / Ad (1080x1920)" },
    ],
  },
];
const imageGenerationModels = [
  { value: "chatgpt-image-2.0", label: "ChatGPT Image 2.0" },
  { value: "gemini-2.5-flash-image", label: "Gemini 2.5 Flash Image" },
  { value: "gemini-3-pro-image-preview", label: "Gemini 3 Pro Image (Preview)" },
  { value: "gemini-3.1-flash-image-preview", label: "Gemini 3.1 Flash Image (Preview)" },
  { value: "nano-banana-pro-preview", label: "Nano Banana 2 (Preview)" },
];

export default function StyleForgeStudio({ onNavigate }: StyleForgeStudioProps) {
  const campaignApi = useCampaignApi();

  const [activeTab, setActiveTab] = useState("analyze");
  const [styles, setStyles] = useState<StyleReference[]>([]);
  const [editingStyle, setEditingStyle] = useState<StyleReference | null>(null);
  const [editingJson, setEditingJson] = useState("");
  const [editingName, setEditingName] = useState("");
  const [editingError, setEditingError] = useState("");

  const [styleImages, setStyleImages] = useState<File[]>([]);
  const [isLoadingSavedStyles, setIsLoadingSavedStyles] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [extractedStyle, setExtractedStyle] = useState<StyleReferenceData | null>(null);
  const [styleName, setStyleName] = useState("");
  const [styleJsonText, setStyleJsonText] = useState("");
  const [isEditingExtractedJson, setIsEditingExtractedJson] = useState(false);
  const [extractedJsonError, setExtractedJsonError] = useState("");

  const [selectedStyleId, setSelectedStyleId] = useState("");
  const [prompt, setPrompt] = useState("");
  const [aspectRatio, setAspectRatio] = useState("1:1");
  const [imageModel, setImageModel] = useState("gemini-2.5-flash-image");
  const [isTransparent, setIsTransparent] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState("#f3f0eb");
  const [objectImages, setObjectImages] = useState<File[]>([]);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const selectedStyle = useMemo(
    () => styles.find((style) => style.id === selectedStyleId) ?? null,
    [selectedStyleId, styles],
  );

  useEffect(() => {
    const loadSavedStyles = async () => {
      setIsLoadingSavedStyles(true);
      try {
        const savedStyles = await campaignApi.listSavedStyles();
        setStyles(savedStyles.map((style) => ({
          id: style.id.toString(),
          name: style.name,
          reference: style.styleReference,
          previewImage: style.previewImageDataUrl ?? undefined,
        })));
      } catch (error) {
        console.error("Failed to load saved styles", error);
        toast.error("Failed to load saved styles.");
      } finally {
        setIsLoadingSavedStyles(false);
      }
    };

  const deleteStyle = async (style: StyleReference) => {
    try {
      await campaignApi.deleteSavedStyle(Number(style.id));
      setStyles((previous) => previous.filter((currentStyle) => currentStyle.id !== style.id));
      if (selectedStyleId === style.id) {
        setSelectedStyleId("");
      }
      toast.success("Style deleted.");
    } catch (error) {
      console.error("Failed to delete style", error);
      toast.error("Failed to delete style.");
    }
  };

    loadSavedStyles();
  }, [campaignApi]);

  const handleStyleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStyleImages(Array.from(event.target.files ?? []));
  };

  const handleObjectImagesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setObjectImages(Array.from(event.target.files ?? []));
  };

  const removeStyleImage = (indexToRemove: number) => {
    setStyleImages((previous) => previous.filter((_, index) => index !== indexToRemove));
  };

  const removeObjectImage = (indexToRemove: number) => {
    setObjectImages((previous) => previous.filter((_, index) => index !== indexToRemove));
  };

  const styleImagePreviews = useMemo(
    () => styleImages.map((file) => ({ file, url: URL.createObjectURL(file) })),
    [styleImages],
  );

  const objectImagePreviews = useMemo(
    () => objectImages.map((file) => ({ file, url: URL.createObjectURL(file) })),
    [objectImages],
  );

  useEffect(() => {
    return () => {
      styleImagePreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [styleImagePreviews]);

  useEffect(() => {
    return () => {
      objectImagePreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [objectImagePreviews]);

  const getPastedImageFiles = (clipboardData: DataTransfer | null): File[] => {
    if (!clipboardData) {
      return [];
    }

    return Array.from(clipboardData.items)
      .filter((item) => item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null);
  };

  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTypingTarget =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable;

      if (isTypingTarget) {
        return;
      }

      const pastedImages = getPastedImageFiles(event.clipboardData);
      if (pastedImages.length === 0) {
        return;
      }

      event.preventDefault();

      if (activeTab === "generate") {
        setObjectImages((previous) => [...previous, ...pastedImages]);
        toast.success(`Pasted ${pastedImages.length} object image(s).`);
        return;
      }

      setStyleImages((previous) => [...previous, ...pastedImages]);
      toast.success(`Pasted ${pastedImages.length} reference image(s).`);
    };

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [activeTab]);

  const extractStyle = async () => {
    if (styleImages.length === 0) {
      toast.error("Upload at least one reference image.");
      return;
    }

    setIsAnalyzing(true);
    setExtractedStyle(null);

    try {
      const formData = new FormData();
      styleImages.forEach((file) => formData.append("images", file));

      const response = await campaignApi.extractStyleReference(formData);
      setExtractedStyle(response.styleReference);
      setStyleName(response.suggestedName?.trim() || `Style ${styles.length + 1}`);
      setStyleJsonText(JSON.stringify(response.styleReference, null, 2));
      setIsEditingExtractedJson(false);
      setExtractedJsonError("");
      toast.success(`Style extracted with ${response.model}.`);
    } catch (error) {
      console.error("Style extraction failed", error);
      toast.error("Failed to extract style. Please try different references.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const fileToDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = String(reader.result ?? "");
        if (!dataUrl) {
          reject(new Error("Could not convert file to data URL"));
          return;
        }

        resolve(dataUrl);
      };
      reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });

  const saveStyle = async () => {
    if (!extractedStyle || !styleName.trim()) {
      toast.error("Provide a style name before saving.");
      return;
    }

    try {
      const previewImageDataUrl = styleImages.length > 0
        ? await fileToDataUrl(styleImages[0])
        : null;

      const savedStyle = await campaignApi.saveStyle({
        name: styleName.trim(),
        styleReference: extractedStyle,
        previewImageDataUrl,
      });

      const style: StyleReference = {
        id: savedStyle.id.toString(),
        name: savedStyle.name,
        reference: savedStyle.styleReference,
        previewImage: savedStyle.previewImageDataUrl ?? undefined,
      };

      setStyles((previous) => [style, ...previous]);
      setSelectedStyleId(style.id);
      setActiveTab("generate");

      setStyleImages([]);
      setExtractedStyle(null);
      setStyleName("");
      setStyleJsonText("");
      setIsEditingExtractedJson(false);
      setExtractedJsonError("");

      toast.success("Style reference saved.");
    } catch (error) {
      console.error("Failed to save style", error);
      toast.error("Failed to save style reference.");
    }
  };

  const applyExtractedJsonChanges = () => {
    try {
      const parsed = JSON.parse(styleJsonText) as StyleReferenceData;
      setExtractedStyle(parsed);
      setIsEditingExtractedJson(false);
      setExtractedJsonError("");
    } catch {
      setExtractedJsonError("Invalid JSON format.");
    }
  };

  const openEditStyle = (style: StyleReference) => {
    setEditingStyle(style);
    setEditingName(style.name);
    setEditingJson(JSON.stringify(style.reference, null, 2));
    setEditingError("");
  };

  const saveEditedStyle = async () => {
    if (!editingStyle) {
      return;
    }

    try {
      const parsedReference = JSON.parse(editingJson) as StyleReferenceData;
      const updatedSavedStyle = await campaignApi.updateSavedStyle(Number(editingStyle.id), {
        name: editingName.trim() || editingStyle.name,
        styleReference: parsedReference,
      });

      const updatedStyle: StyleReference = {
        id: updatedSavedStyle.id.toString(),
        name: updatedSavedStyle.name,
        reference: updatedSavedStyle.styleReference,
        previewImage: updatedSavedStyle.previewImageDataUrl ?? editingStyle.previewImage,
      };

      setStyles((previous) => previous.map((style) => (style.id === updatedStyle.id ? updatedStyle : style)));
      if (selectedStyleId === updatedStyle.id) {
        setSelectedStyleId(updatedStyle.id);
      }
      setEditingStyle(null);
      setEditingError("");
      toast.success("Style updated.");
    } catch {
      setEditingError("Invalid JSON format.");
    }
  };

  const deleteStyle = async (style: StyleReference) => {
    try {
      await campaignApi.deleteSavedStyle(Number(style.id));
      setStyles((previous) => previous.filter((currentStyle) => currentStyle.id !== style.id));
      if (selectedStyleId === style.id) {
        setSelectedStyleId("");
      }
      toast.success("Style deleted.");
    } catch (error) {
      console.error("Failed to delete style", error);
      toast.error("Failed to delete style.");
    }
  };

  const copyJson = async (value: unknown) => {
    await navigator.clipboard.writeText(JSON.stringify(value, null, 2));
    toast.success("JSON copied to clipboard.");
  };

  const fileToInlineImage = (file: File): Promise<StyleForgeInlineImage> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = String(reader.result ?? "").split(",")[1] ?? "";
        if (!base64) {
          reject(new Error("Could not encode file"));
          return;
        }

        resolve({
          data: base64,
          mimeType: file.type || "image/png",
        });
      };
      reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });

  const generateImage = async () => {
    if (!selectedStyle) {
      toast.error("Select a saved style first.");
      return;
    }

    if (!prompt.trim()) {
      toast.error("Enter a prompt for image generation.");
      return;
    }

    setIsGenerating(true);
    setGeneratedImage(null);

    try {
      const objectImagePayload = await Promise.all(objectImages.map((file) => fileToInlineImage(file)));

      const response = await campaignApi.generateStyledImage({
        styleReference: selectedStyle.reference,
        prompt: prompt.trim(),
        aspectRatio,
        model: imageModel,
        isTransparent,
        backgroundColor,
        objectImages: objectImagePayload.length > 0 ? objectImagePayload : undefined,
      });

      setGeneratedImage(`data:${response.mimeType};base64,${response.imageBase64}`);
      if (response.warnings?.length) {
        response.warnings.forEach((warning) => toast.warning(warning));
      } else {
        toast.success(`Image generated with ${response.model}.`);
      }
    } catch (error) {
      console.error("Image generation failed", error);
      toast.error("Failed to generate image.");
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadGeneratedImage = () => {
    if (!generatedImage) {
      return;
    }

    const link = document.createElement("a");
    link.href = generatedImage;
    link.download = `styleforge-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-[#f8f5f0] p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-semibold">StyleForge Studio</h1>
              <p className="text-sm text-muted-foreground">
                Extract reusable visual styles from references and generate new images in that style.
              </p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="analyze" className="flex items-center gap-2">
              <Palette className="h-4 w-4" />
              Extract Style
            </TabsTrigger>
            <TabsTrigger value="generate" className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              Generate Image
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analyze" className="space-y-4 pt-4">
            <Card>
              <CardHeader>
                <CardTitle>Style extraction</CardTitle>
                <CardDescription>Upload one or more visual references to extract a unified style JSON.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="style-images">Reference images</Label>
                  <Input id="style-images" type="file" accept="image/*" multiple onChange={handleStyleFilesChange} />
                  <p className="text-xs text-muted-foreground">Tip: paste images with Ctrl+V (or Cmd+V).</p>
                  {styleImages.length > 0 && (
                    <p className="text-xs text-muted-foreground">{styleImages.length} file(s) selected.</p>
                  )}
                  {styleImagePreviews.length > 0 && (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                      {styleImagePreviews.map((preview, index) => (
                        <div key={`${preview.file.name}-${preview.file.lastModified}-${index}`} className="space-y-1">
                          <div className="relative overflow-hidden rounded-md border bg-background">
                            <img src={preview.url} alt={preview.file.name} className="aspect-square w-full object-cover" />
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              className="absolute right-1 top-1 h-7 w-7"
                              onClick={() => removeStyleImage(index)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                          <p className="truncate text-xs text-muted-foreground">{preview.file.name}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <CapabilityButton capability={P.CampaignStyleExtract} onClick={extractStyle} disabled={styleImages.length === 0 || isAnalyzing}>
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Analyzing
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Extract Style
                    </>
                  )}
                </CapabilityButton>

                {extractedStyle && (
                  <div className="space-y-3 rounded-md border bg-muted/40 p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium">Extracted style</h3>
                      <div className="flex items-center gap-2">
                        {!isEditingExtractedJson && (
                          <Button variant="outline" size="sm" onClick={() => setIsEditingExtractedJson(true)}>
                            <Edit2 className="mr-2 h-3 w-3" />
                            Edit JSON
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => copyJson(extractedStyle)}>
                          <Copy className="mr-2 h-3 w-3" />
                          Copy JSON
                        </Button>
                      </div>
                    </div>

                    {isEditingExtractedJson ? (
                      <div className="space-y-2">
                        <Textarea
                          value={styleJsonText}
                          onChange={(event) => setStyleJsonText(event.target.value)}
                          className="min-h-[280px] font-mono text-xs"
                        />
                        {extractedJsonError && <p className="text-xs text-red-500">{extractedJsonError}</p>}
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => setIsEditingExtractedJson(false)}>
                            <X className="mr-2 h-3 w-3" />
                            Cancel
                          </Button>
                          <Button size="sm" onClick={applyExtractedJsonChanges}>
                            <Save className="mr-2 h-3 w-3" />
                            Apply
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <pre className="max-h-80 overflow-auto rounded-md bg-background p-3 text-xs">
                          {JSON.stringify(extractedStyle, null, 2)}
                        </pre>
                        <div className="flex items-end gap-2">
                          <div className="flex-1 space-y-1">
                            <Label htmlFor="style-name">Style name</Label>
                            <Input
                              id="style-name"
                              value={styleName}
                              onChange={(event) => setStyleName(event.target.value)}
                              placeholder="e.g. Warm Editorial Illustration"
                            />
                          </div>
                          <CapabilityButton capability={P.CampaignStyleCreate} onClick={saveStyle}>Save style</CapabilityButton>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="generate" className="space-y-4 pt-4">
            <Card>
              <CardHeader>
                <CardTitle>Image generation</CardTitle>
                <CardDescription>Use a saved style plus prompt and optional object images.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="style-select">Style</Label>
                    <select
                      id="style-select"
                      value={selectedStyleId}
                      onChange={(event) => setSelectedStyleId(event.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="">Select style</option>
                      {styles.map((style) => (
                        <option key={style.id} value={style.id}>
                          {style.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="aspect-ratio">Aspect ratio</Label>
                    <select
                      id="aspect-ratio"
                      value={aspectRatio}
                      onChange={(event) => setAspectRatio(event.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      {aspectRatioGroups.map((group) => (
                        <optgroup key={group.label} label={group.label}>
                          {group.options.map((option) => (
                            <option key={`${group.label}-${option.label}-${option.value}`} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="image-model">Image model</Label>
                    <select
                      id="image-model"
                      value={imageModel}
                      onChange={(event) => setImageModel(event.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      {imageGenerationModels.map((modelOption) => (
                        <option key={modelOption.value} value={modelOption.value}>
                          {modelOption.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="generation-prompt">Prompt</Label>
                  <Textarea
                    id="generation-prompt"
                    value={prompt}
                    onChange={(event) => setPrompt(event.target.value)}
                    placeholder="Describe the image you want in the selected style."
                    rows={4}
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="object-images">Object images (optional)</Label>
                    <Input id="object-images" type="file" accept="image/*" multiple onChange={handleObjectImagesChange} />
                    <p className="text-xs text-muted-foreground">Tip: paste images with Ctrl+V (or Cmd+V).</p>
                    {objectImages.length > 0 && (
                      <p className="text-xs text-muted-foreground">{objectImages.length} object image(s) selected.</p>
                    )}
                    {objectImagePreviews.length > 0 && (
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                        {objectImagePreviews.map((preview, index) => (
                          <div key={`${preview.file.name}-${preview.file.lastModified}-${index}`} className="space-y-1">
                            <div className="relative overflow-hidden rounded-md border bg-background">
                              <img src={preview.url} alt={preview.file.name} className="aspect-square w-full object-cover" />
                              <Button
                                type="button"
                                variant="destructive"
                                size="icon"
                                className="absolute right-1 top-1 h-7 w-7"
                                onClick={() => removeObjectImage(index)}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                            <p className="truncate text-xs text-muted-foreground">{preview.file.name}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label>Background</Label>
                    <div className="flex items-center gap-4 rounded-md border p-3">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={isTransparent}
                          onChange={(event) => setIsTransparent(event.target.checked)}
                        />
                        Transparent target
                      </label>
                      {!isTransparent && (
                        <Input
                          value={backgroundColor}
                          onChange={(event) => setBackgroundColor(event.target.value)}
                          className="max-w-[140px]"
                        />
                      )}
                    </div>
                  </div>
                </div>

                <CapabilityButton capability={P.CampaignImageGenerate} onClick={generateImage} disabled={isGenerating || !selectedStyleId || !prompt.trim()}>
                  {isGenerating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generate Image
                    </>
                  )}
                </CapabilityButton>

                {generatedImage && (
                  <div className="space-y-3 rounded-md border bg-muted/40 p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium">Generated result</h3>
                      <Button variant="outline" size="sm" onClick={downloadGeneratedImage}>
                        <Download className="mr-2 h-3 w-3" />
                        Download PNG
                      </Button>
                    </div>
                    <div className="overflow-hidden rounded-md border bg-background p-2">
                      <img src={generatedImage} alt="Generated styleforge output" className="w-full object-contain" />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {styles.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Saved styles</CardTitle>
              <CardDescription>Reusable style references extracted from your uploaded examples.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {styles.map((style) => (
                  <div key={style.id} className="relative flex h-full flex-col space-y-3 rounded-lg border bg-white p-4">
                    <CapabilityButton capability={P.CampaignStyleDelete}
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute -right-3 -top-3 z-10 h-8 w-8 rounded-full shadow-md"
                      onClick={() => void deleteStyle(style)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </CapabilityButton>
                    {style.previewImage && (
                      <img src={style.previewImage} alt={style.name} className="aspect-square w-full rounded-md object-cover" />
                    )}
                    <div className="space-y-1">
                      <h3 className="font-medium">{style.name}</h3>
                      <p className="line-clamp-2 text-xs text-muted-foreground">
                        {style.reference.overallStyleDescription || "No description"}
                      </p>
                    </div>
                    <div className="mt-auto flex flex-wrap items-center gap-2 border-t pt-3">
                      <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => copyJson(style.reference)}>
                        <Copy className="mr-1.5 h-3.5 w-3.5" />
                        Json
                      </Button>
                      <CapabilityButton capability={P.CampaignStyleUpdate} type="button" variant="outline" size="sm" className="h-8" onClick={() => openEditStyle(style)}>
                        <Edit2 className="mr-1.5 h-3.5 w-3.5" />
                        Edit
                      </CapabilityButton>
                      <Button
                        type="button"
                        size="sm"
                        className="h-8"
                        onClick={() => {
                          setSelectedStyleId(style.id);
                          setActiveTab("generate");
                          toast.success(`Selected ${style.name}.`);
                        }}
                      >
                        <Check className="mr-1.5 h-3.5 w-3.5" />
                        Use
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        {isLoadingSavedStyles && (
          <p className="text-sm text-muted-foreground">Loading saved styles...</p>
        )}
      </div>

      <Dialog open={!!editingStyle} onOpenChange={(open) => !open && setEditingStyle(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit style</DialogTitle>
            <DialogDescription>Adjust style name and JSON reference.</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="edit-style-name">Style name</Label>
              <Input id="edit-style-name" value={editingName} onChange={(event) => setEditingName(event.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-style-json">Style JSON</Label>
              <Textarea
                id="edit-style-json"
                className="min-h-[360px] font-mono text-xs"
                value={editingJson}
                onChange={(event) => setEditingJson(event.target.value)}
              />
              {editingError && <p className="text-xs text-red-500">{editingError}</p>}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingStyle(null)}>
              Cancel
            </Button>
            <CapabilityButton capability={P.CampaignStyleUpdate} onClick={saveEditedStyle}>Save changes</CapabilityButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
